import { createScopedLogger, useApiClient, useErrorHandler, useToastService } from "@@common";
import { AuthContext } from "@@components";
import { useQueryClient } from "@tanstack/react-query";
import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { ConversationClient, OperationClient } from "../../api/api";
import Spinner from "../../common/components/Spinner";
import { VariableNames } from "../../UserInterfaceModule/Constants/VariableNames";
import BundleContext from "../../UserInterfaceModule/Contexts/BundleContext";
import { NavigationService } from "../../UserInterfaceModule/Services/NavigationService";
import { NotifyService } from "../../UserInterfaceModule/Services/NotifyService";
import { IExpressionEvaluatorContext } from "../Models/IExpressionEvaluatorContext";
import { BundleServicesKey } from "../Runtime/Evaluators/DomainServices/DomainServiceEnumItemsExpressionEvaluator";
import { ApiServices, ApiServicesKey } from "../Runtime/Evaluators/DomainServices/DomainServiceOperationCallExpressionEvaluator";
import { ExpressionEvaluatorContext as ExpressionEvaluatorContextClass } from "../Runtime/ExpressionEvaluatorContext";
import { ObjectVariableScope } from "../Runtime/VariableScopes/ObjectVariableScope";
import { VariableScopeObserver } from "../Runtime/VariableScopes/VariableScopeObserver";
import ExpressionEvaluatorContext from "./ExpressionEvaluatorContext";

const logger = createScopedLogger("ExpressionEvaluatorContextProvider");

interface ExpressionEvaluatorContextProviderProps extends PropsWithChildren {
  logChanges?: boolean;
}

export const ExpressionEvaluatorContextProvider: React.FC<ExpressionEvaluatorContextProviderProps> = ({ children, logChanges = false }) => {
  const [context, setContext] = useState<IExpressionEvaluatorContext>();
  const [isPending, setIsPending] = useState(true);
  const hasInitializedRef = useRef(false);

  const auth = useContext(AuthContext);

  const errorHandler = useErrorHandler();
  const operationClient = useApiClient(OperationClient);
  const conversationClient = useApiClient(ConversationClient);
  const queryClient = useQueryClient();
  const bundle = useContext(BundleContext);
  const toastService = useToastService();

  useEffect(() => {
    // Prevent double-initialization in React StrictMode (development only)
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    async function initializeContext() {
      setIsPending(true);
      try {
        const variableScope = new ObjectVariableScope();

        const variableScopeObserver: VariableScopeObserver = (target, name, value) => {
          logger.debug(`Variable changed '${name}' changed`, value, variableScope);
        };

        const expressionContext = new ExpressionEvaluatorContextClass(variableScope);

        expressionContext.SetProperty<ApiServices>(ApiServicesKey, { operationClient, conversationClient, queryClient, errorHandler });
        // Bundle will be set in a separate useEffect that tracks bundle changes
        expressionContext.SetProperty(BundleServicesKey, { bundle });
        expressionContext.SetProperty(NavigationService.KEY, new NavigationService());
        expressionContext.SetProperty(NotifyService.KEY, new NotifyService());

        const ctxVariableScope = new ObjectVariableScope();

        const sessionVariableScope = new ObjectVariableScope();
        const userVariableScope = new ObjectVariableScope();
        userVariableScope.set("userId", auth?.account?.localAccountId ?? null);

        if (logChanges) {
          ctxVariableScope.addObserver(variableScopeObserver);
          userVariableScope.addObserver(variableScopeObserver);
          sessionVariableScope.addObserver(variableScopeObserver);
        }

        expressionContext.VariableScope.set(VariableNames.Context, ctxVariableScope);
        expressionContext.VariableScope.set(VariableNames.Session, sessionVariableScope);
        expressionContext.VariableScope.set(VariableNames.User, userVariableScope);

        if (logChanges) {
          variableScope.addObserver(variableScopeObserver);
        }

        setContext(expressionContext);
      } catch (error) {
        await errorHandler(error);
      } finally {
        setIsPending(false);
      }
    }

    initializeContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.account?.localAccountId]); // Initialize once on mount

  // Update bundle in expression context whenever it changes
  // Include bundle.Id in dependencies to ensure the effect runs when the bundle actually changes
  useEffect(() => {
    if (!context) return;
    logger.debug("Updating bundle in context, bundle.Id:", bundle.Id, "bundle.CurrentAppName:", bundle.CurrentAppName);
    context.SetProperty(BundleServicesKey, { bundle });
  }, [context, bundle, bundle.Id, bundle.CurrentAppName]);

  useEffect(() => {
    if (!context) return;
    context.GetProperty<NotifyService>(NotifyService.KEY)?.setToastService(toastService);
  }, [context, toastService]);

  if (isPending || context === undefined) {
    return <Spinner />;
  }

  return <ExpressionEvaluatorContext.Provider value={context}>{children}</ExpressionEvaluatorContext.Provider>;
};

export default ExpressionEvaluatorContextProvider;
